import { deviceApi } from '@/api'
import {
	type DeviceFilterOption,
	type DeviceSearchFilters,
	type DeviceSearchSortBy,
	DeviceStatus,
	type SortOrder,
} from '@/api/deviceApi'
import { projectsApi } from '@/api/projectsApi'
import type { OrganizationDevice } from '@/types/organization'
import { isPageBeyondTotal } from '@/utils/deviceFieldUtils'
import { type PageError, isAbortError, toPageError } from '@/utils/errorHandler'
import { create } from 'zustand'

const inFlightOrgFetches = new Set<string>()
const inFlightTenantNameFetches = new Set<string>()
// Shared across fetchDevices + searchDevices so a newer list request cancels a
// superseded one (latest-wins). Important for the 2s auto-refresh poll: an in-flight
// silent refresh must not overwrite a fresher result from a user pagination/search.
let orgListAbortController: AbortController | null = null

interface OrgDeviceStore {
	devices: OrganizationDevice[]
	totalCount: number
	loading: boolean
	error: PageError | null
	currentPage: number
	pageSize: number
	tabCounts: { active: number; inactive: number; all: number }
	tenantNameMap: Record<string, string>
	tenantNameOrgId: string | null
	currentOrgId: string | null
	currentStatusFilter: DeviceStatus
	/** Active server-side filters (id/metadata/state); only `null` means unfiltered — a bare `false` is a real
	 *  filter that matches nothing. Re-applied on pagination. */
	searchFilters: DeviceSearchFilters | null
	deviceFilterOptions: DeviceFilterOption[]
	// Server-side sort. Default 'uid' asc preserves the prior fixed ordering.
	sortBy: DeviceSearchSortBy
	sortOrder: SortOrder

	fetchDevices: (
		orgId: string,
		statusFilter?: DeviceStatus,
		page?: number,
		pageSize?: number,
		silent?: boolean,
	) => Promise<void>
	searchDevices: (
		orgId: string,
		filters: DeviceSearchFilters | null,
		page?: number,
		pageSize?: number,
		statusFilter?: DeviceStatus,
		silent?: boolean,
	) => Promise<void>
	fetchDeviceFilterOptions: (orgId: string, status?: DeviceStatus) => Promise<void>
	// Fetch the count for the tab NOT currently shown so both tab counts stay populated
	// without requiring a tab click. fetchDevices already sets the current tab's count.
	refreshTabCounts: (orgId: string, currentStatus?: DeviceStatus) => Promise<void>
	fetchTenantNames: (orgId: string) => Promise<void>
	reEnrichDevicesWithTenantNames: () => void
	setPage: (page: number, statusFilter?: DeviceStatus) => void
	setPageSize: (size: number, statusFilter?: DeviceStatus) => void
	setSort: (sortBy: DeviceSearchSortBy, sortOrder: SortOrder) => void
	/** The project (or organization) the current sort was restored for. */
	sortScope: string | null
	restoreSort: (sortBy: DeviceSearchSortBy, sortOrder: SortOrder, scope: string) => void
	resetPagination: () => void
}

export const useOrgDeviceStore = create<OrgDeviceStore>((set, get) => {
	// Computes totalPages for this response and, if `page` is beyond it, snaps currentPage back
	// and returns the corrected page for the caller to retry at; returns null when in range.
	const snapToLastValidPage = (page: number, count: number, pageSize: number): number | null => {
		const totalPages = count > 0 ? Math.ceil(count / pageSize) : 1
		if (!isPageBeyondTotal(page, count, totalPages)) {
			return null
		}
		set({ currentPage: totalPages })
		return totalPages
	}

	return {
		devices: [],
		totalCount: 0,
		loading: false,
		error: null,
		currentPage: 1,
		pageSize: 20,
		tabCounts: { active: 0, inactive: 0, all: 0 },
		tenantNameMap: {},
		tenantNameOrgId: null,
		currentOrgId: null,
		currentStatusFilter: DeviceStatus.Active,
		searchFilters: null,
		deviceFilterOptions: [],
		sortBy: 'uid',
		sortOrder: 'asc',
		sortScope: null,

		fetchDevices: async (
			orgId: string,
			statusFilter?: DeviceStatus,
			page?: number,
			pageSize?: number,
			silent?: boolean,
		) => {
			const state = get()
			const effectiveStatus = statusFilter ?? state.currentStatusFilter
			const effectivePage = page ?? state.currentPage
			const effectivePageSize = pageSize ?? state.pageSize
			// Include sort in the dedup key so a sort change isn't swallowed by a concurrent
			// auto-refresh poll that's already in flight for the same page/status.
			const requestKey = `${orgId}:${effectiveStatus}:${effectivePage}:${effectivePageSize}:${JSON.stringify(state.sortBy)}:${state.sortOrder}`

			// Deduplicate identical overlapping requests (e.g. StrictMode double effects).
			if (inFlightOrgFetches.has(requestKey)) {
				return
			}
			try {
				inFlightOrgFetches.add(requestKey)
				// Cancel any in-flight list request (a superseded fetch or search) — latest-wins.
				orgListAbortController?.abort()
				orgListAbortController = new AbortController()
				// Silent (poll) refresh skips the loading flash.
				set(silent ? { error: null } : { loading: true, error: null })

				const currentRes = await deviceApi.searchOrganizationDevices(
					orgId,
					{
						page: effectivePage,
						perPage: effectivePageSize,
						status: effectiveStatus,
						sortBy: state.sortBy,
						sortOrder: state.sortOrder,
					},
					orgListAbortController.signal,
				)

				const snappedPage = snapToLastValidPage(effectivePage, currentRes.count, effectivePageSize)
				if (snappedPage !== null) {
					inFlightOrgFetches.delete(requestKey)
					await get().fetchDevices(orgId, effectiveStatus, snappedPage, effectivePageSize, silent)
					return
				}

				const { tenantNameMap } = get()
				const enrichedDevices = currentRes.devices.map(device => ({
					...device,
					tenantName: device.tenantName || tenantNameMap[device.tenant_id] || device.tenant_id,
				}))
				const nextTabCounts = { ...get().tabCounts, [effectiveStatus]: currentRes.count }

				set({
					devices: enrichedDevices,
					totalCount: currentRes.count,
					loading: false,
					currentOrgId: orgId,
					currentStatusFilter: effectiveStatus,
					currentPage: effectivePage,
					pageSize: effectivePageSize,
					tabCounts: nextTabCounts,
					searchFilters: null,
				})
			} catch (err) {
				// Superseded by a newer list request — let the newer one own the state.
				if (isAbortError(err)) {
					return
				}
				console.error('Failed to fetch organization devices:', err)
				set({ loading: false, error: toPageError(err, 'Failed to load devices') })
			} finally {
				inFlightOrgFetches.delete(requestKey)
			}
		},

		searchDevices: async (orgId, filters, page, pageSize, statusFilter, silent) => {
			try {
				const state = get()
				const effectiveStatus = statusFilter ?? state.currentStatusFilter
				const effectivePage = page ?? state.currentPage
				const effectivePageSize = pageSize ?? state.pageSize
				// Cancel any in-flight list request (a superseded fetch or search) — latest-wins.
				orgListAbortController?.abort()
				orgListAbortController = new AbortController()
				// Silent refresh (auto-poll) re-runs the active search without flashing loading.
				set(silent ? { searchFilters: filters, error: null } : { loading: true, searchFilters: filters, error: null })

				const res = await deviceApi.searchOrganizationDevices(
					orgId,
					{
						page: effectivePage,
						perPage: effectivePageSize,
						status: effectiveStatus,
						sortBy: state.sortBy,
						sortOrder: state.sortOrder,
						filters,
					},
					orgListAbortController.signal,
				)

				const snappedPage = snapToLastValidPage(effectivePage, res.count, effectivePageSize)
				if (snappedPage !== null) {
					await get().searchDevices(orgId, filters, snappedPage, effectivePageSize, effectiveStatus, silent)
					return
				}

				const { tenantNameMap } = get()
				const enrichedDevices = res.devices.map(device => ({
					...device,
					tenantName: device.tenantName || tenantNameMap[device.tenant_id] || device.tenant_id,
				}))

				set({
					devices: enrichedDevices,
					totalCount: res.count,
					loading: false,
					currentOrgId: orgId,
					currentStatusFilter: effectiveStatus,
					currentPage: effectivePage,
					pageSize: effectivePageSize,
				})
			} catch (err) {
				// Superseded by a newer list request — let the newer one own the state.
				if (isAbortError(err)) {
					return
				}
				console.error('Failed to search organization devices:', err)
				set({ loading: false, error: toPageError(err, 'Failed to search devices') })
			}
		},

		fetchDeviceFilterOptions: async (orgId: string, status: DeviceStatus = DeviceStatus.Active) => {
			try {
				const options = await deviceApi.getOrgDeviceFilterOptions(orgId, status)
				set({ deviceFilterOptions: options })
			} catch (err) {
				console.error('Failed to fetch organization device filter options:', err)
			}
		},

		refreshTabCounts: async (orgId: string, currentStatus?: DeviceStatus) => {
			try {
				if (currentStatus) {
					const res = await deviceApi.searchOrganizationDevices(orgId, {
						page: 1,
						perPage: 1,
						status: currentStatus,
						sortBy: 'uid',
					})
					set(state => ({ tabCounts: { ...state.tabCounts, [currentStatus]: res.count } }))
				} else {
					const [allRes, activeRes, inactiveRes] = await Promise.allSettled([
						deviceApi.searchOrganizationDevices(orgId, {
							page: 1,
							perPage: 1,
							status: DeviceStatus.All,
							sortBy: 'uid',
						}),
						deviceApi.searchOrganizationDevices(orgId, {
							page: 1,
							perPage: 1,
							status: DeviceStatus.Active,
							sortBy: 'uid',
						}),
						deviceApi.searchOrganizationDevices(orgId, {
							page: 1,
							perPage: 1,
							status: DeviceStatus.Inactive,
							sortBy: 'uid',
						}),
					])
					const patch: Partial<{ active: number; inactive: number; all: number }> = {}
					if (allRes.status === 'fulfilled') {
						patch.all = allRes.value.count
					}
					if (activeRes.status === 'fulfilled') {
						patch.active = activeRes.value.count
					}
					if (inactiveRes.status === 'fulfilled') {
						patch.inactive = inactiveRes.value.count
					}
					set(state => ({ tabCounts: { ...state.tabCounts, ...patch } }))
				}
			} catch (err) {
				console.error('Failed to refresh organization tab counts:', err)
			}
		},

		fetchTenantNames: async (orgId: string) => {
			const state = get()
			if (state.tenantNameOrgId === orgId && Object.keys(state.tenantNameMap).length > 0) {
				// Re-enrich existing devices with tenant names even if we skip the fetch
				get().reEnrichDevicesWithTenantNames()
				return
			}
			if (inFlightTenantNameFetches.has(orgId)) {
				return
			}
			inFlightTenantNameFetches.add(orgId)
			try {
				const nameMap = await projectsApi.listProjectNames(orgId)
				// Convert Map to Record for serialization safety
				const record: Record<string, string> = {}
				if (nameMap instanceof Map) {
					nameMap.forEach((v, k) => {
						record[k] = v
					})
				} else {
					Object.assign(record, nameMap)
				}
				set({ tenantNameMap: record, tenantNameOrgId: orgId })
				get().reEnrichDevicesWithTenantNames()
			} catch (err) {
				console.error('Failed to fetch tenant names:', err)
			} finally {
				inFlightTenantNameFetches.delete(orgId)
			}
		},

		reEnrichDevicesWithTenantNames: () => {
			const { devices, tenantNameMap } = get()
			if (devices.length === 0 || Object.keys(tenantNameMap).length === 0) {
				return
			}
			// A device committed before the names arrived carries its own tenant_id as tenantName, so an
			// `||` on tenantName would keep the raw id forever. Only that fallback is replaced; a name the
			// API itself supplied still wins, as it does at commit time.
			let changed = false
			const reEnrichedDevices = devices.map(device => {
				if (device.tenantName && device.tenantName !== device.tenant_id) {
					return device
				}
				const resolved = tenantNameMap[device.tenant_id]
				if (!resolved || resolved === device.tenantName) {
					return device
				}
				changed = true
				return { ...device, tenantName: resolved }
			})
			if (changed) {
				set({ devices: reEnrichedDevices })
			}
		},

		setPage: (page: number, statusFilter?: DeviceStatus) => {
			const { currentOrgId, currentPage, pageSize, currentStatusFilter, searchFilters } = get()
			if (page === currentPage) {
				return
			}
			set({ currentPage: page })
			if (!currentOrgId) {
				return
			}
			const status = statusFilter ?? currentStatusFilter
			if (searchFilters !== null) {
				void get().searchDevices(currentOrgId, searchFilters, page, pageSize, status)
			} else {
				void get().fetchDevices(currentOrgId, status, page, pageSize)
			}
		},

		setPageSize: (size: number, statusFilter?: DeviceStatus) => {
			const { currentOrgId, currentStatusFilter, searchFilters } = get()
			set({ pageSize: size, currentPage: 1 })
			if (!currentOrgId) {
				return
			}
			const status = statusFilter ?? currentStatusFilter
			if (searchFilters !== null) {
				void get().searchDevices(currentOrgId, searchFilters, 1, size, status)
			} else {
				void get().fetchDevices(currentOrgId, status, 1, size)
			}
		},

		/** Applies a sort without fetching. Route `beforeLoad` calls it, so the page's first fetch already uses it. */
		restoreSort: (sortBy: DeviceSearchSortBy, sortOrder: SortOrder, scope: string) => {
			const current = get()
			if (
				current.sortScope === scope &&
				current.sortOrder === sortOrder &&
				JSON.stringify(current.sortBy) === JSON.stringify(sortBy)
			) {
				return
			}
			set({ sortBy, sortOrder, sortScope: scope, currentPage: 1 })
		},

		setSort: (sortBy: DeviceSearchSortBy, sortOrder: SortOrder) => {
			const { currentOrgId, pageSize, currentStatusFilter, searchFilters } = get()
			// Sort spans the whole dataset, so jump back to page 1 and refetch from the server.
			set({ sortBy, sortOrder, currentPage: 1 })
			if (!currentOrgId) {
				return
			}
			if (searchFilters !== null) {
				void get().searchDevices(currentOrgId, searchFilters, 1, pageSize, currentStatusFilter)
			} else {
				void get().fetchDevices(currentOrgId, currentStatusFilter, 1, pageSize)
			}
		},

		// Page size is the user's saved preference, not navigation state, so the reset leaves it alone.
		resetPagination: () => {
			set({ currentPage: 1 })
		},
	}
})
